import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiBell, FiUser, FiPlus, FiList, FiLogOut } from 'react-icons/fi';
import ProfileModal from '../components/ProfileModal';
import NotificationsModal from '../components/NotificationsModal';
import LogoutModal from '../components/LogoutModal';

const HomeContainer = styled(motion.div)`
  padding: 20px;
  background-color:rgb(242, 244, 245);
  min-height: 100vh;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  background: white;
  padding: 15px;
  border-radius: 16px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.28);
    border: 0.5px solid rgb(207, 49, 49);
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const LogoCircle = styled(motion.div)`
  width: 45px;
  height: 45px;
  background-color: #ff0000;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 10px;
  text-align: center;
  line-height: 1.2;
  cursor: pointer;

  span {
    font-weight: 800;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const Greeting = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
`;

const UserName = styled.h2`
  font-size: 18px;
  color: #333;
  font-weight: 600;
`;

const IconGroup = styled.div`
  display: flex;
  gap: 5px;
`;

const IconButton = styled(motion.button)`
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 20px;
  cursor: pointer;
  position: relative;
  
  &:hover {
    background: #f5f5f5;
    color: #ff0000;
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  background: #ff0000;
  color: white;
  font-size: 12px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 20px;
  max-width: 500px;
  margin: 0 auto;
  min-height: calc(100vh - 180px);
  align-content: start;
  perspective: 1000px;

  @media (max-width: 768px) {
    gap: 20px;
    padding: 15px;
    min-height: calc(100vh - 150px);
  }
`;

const Card = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 24px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  transform-style: preserve-3d;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(
      45deg,
      rgba(255, 0, 0, 0.05),
      rgba(255, 255, 255, 0.1),
      rgba(255, 0, 0, 0.05)
    );
    background-size: 200% 200%;
    animation: gradientMove 8s linear infinite;
    border-radius: 20px;
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  &:hover::before {
    opacity: 1;
  }
  
  @media (max-width: 768px) {
    padding: 20px;
    max-height: 280px;
  }
  
  &:hover {
    transform: translateY(-5px) rotateX(2deg);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    border-color: rgba(255, 0, 0, 0.2);
  }
`;

const CardTitle = styled.h3`
  font-size: 18px;
  color: #333;
  margin-bottom: 10px;
  font-weight: 600;
  position: relative;
  padding-left: 15px;
  transform-style: preserve-3d;
  transition: transform 0.3s ease;

  ${Card}:hover & {
    transform: translateZ(20px);
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 25px;
    background: #ff0000;
    border-radius: 2px;
    box-shadow: 0 0 10px rgba(255, 0, 0, 0.3);
  }
`;

const CardImage = styled.div`
  width: 100%;
  padding-bottom: 60%; /* This creates a 5:3 aspect ratio */
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: #f8f8f8;
  margin-top: 20px;
  transform-style: preserve-3d;
  transition: transform 0.3s ease;
  
  ${Card}:hover & {
    transform: translateZ(30px);
  }
  
  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.05) 100%
    );
    transition: all 0.3s ease;
  }
  
  ${Card}:hover & img {
    transform: scale(1.1);
  }
  
  ${Card}:hover &::after {
    background: linear-gradient(
      to bottom,
      rgba(255, 0, 0, 0) 0%,
      rgba(255, 0, 0, 0.05) 100%
    );
  }

  @media (max-width: 768px) {
    margin-top: 15px;
  }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  color: #333;
  margin: 20px;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 768px) {
    font-size: 20px;
    margin: 15px;
  }
`;

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const Home = () => {
  const navigate = useNavigate();
  const [showProfile, setShowProfile] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [greeting, setGreeting] = useState('Welcome back');

  useEffect(() => {
    const getGreeting = () => {
      const hour = new Date().getHours();
      if (hour > 12) return 'Good Morning';
      if (hour < 17) return 'Good Afternoon';
      return 'Good Evening';
    };
    setGreeting(getGreeting());
  }, []);

  const handleLogout = () => {
    navigate('/login');
  };

  return (
    <HomeContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <Header>
        <Logo>
          <LogoCircle
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            STYLE<span>BOX</span>
          </LogoCircle>
          <UserInfo>
            <Greeting>{greeting},</Greeting>
            <UserName>User name</UserName>
          </UserInfo>
        </Logo>
        
        <IconGroup>
          {/* <IconButton
            onClick={() => setShowNotifications(true)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiBell />
            <NotificationBadge>3</NotificationBadge>
          </IconButton>
          <IconButton
            onClick={() => setShowProfile(true)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiUser />
          </IconButton> */}
          <IconButton
            onClick={() => setShowLogoutConfirm(true)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiLogOut />
          </IconButton>
        </IconGroup>
      </Header>

      <PageTitle>Home</PageTitle>

      <Content>
        <Card
          onClick={() => navigate('/new-enquiry')}
          whileTap={{ scale: 0.98 }}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          custom={0}
        >
          <CardTitle>Create Enquiry</CardTitle>
          <CardImage>
            <img src={'/images/NewEnquiry.jpg'} alt="New Enquiry" />
          </CardImage>
        </Card>

        <Card
          onClick={() => navigate('/view-enquiries')}
          whileTap={{ scale: 0.98 }}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          custom={1}
        >
          <CardTitle>View Enquiries</CardTitle>
          <CardImage>
            <img src={'/images/ViewEnquiry.jpg'} alt="View Enquiries" />
          </CardImage>
        </Card>
      </Content>

      {showLogoutConfirm && (
        <LogoutModal onClose={() => setShowLogoutConfirm(false)} />
      )}

      {showProfile && (
        <ProfileModal onClose={() => setShowProfile(false)} />
      )}
      
      {showNotifications && (
        <NotificationsModal onClose={() => setShowNotifications(false)} />
      )}
    </HomeContainer>
  );
};

export default Home; 